import React, { useContext, useEffect, useState } from "react";
import ButtonGroup from "components/Buttons/ButtonGroup";
import { forceInt, updateParams } from "Utils";
import { ViewListIcon, ViewGridIcon } from "@heroicons/react/outline";
import { useHistory, useLocation } from "react-router-dom";
import { DashboardContext } from "context/Dashboard";
import { useSelector } from "react-redux";
import { useContextualHelp } from "hooks/Help";

export default function Filterbar(props) {
    const history = useHistory();
    const location = useLocation();
    const contextualHelp = useContextualHelp();

    const { queryParams } = useContext(DashboardContext);
    const { workflows, collections } = useSelector(
        (state) => state?.contentFlow
    );
    const [isFiltered, setIsFiltered] = useState(false);

    const filterItems = {
        status: [
            {
                label: "Draft",
                value: "draft",
            },
            {
                label: "Published",
                value: "published",
            },
            {
                label: "Scheduled",
                value: "scheduled",
            },
        ],
    };

    const typeFilters = [
        {
            label: "Article",
            key: "article",
            onClick: () => {
                history.replace(
                    updateParams(location, { type: "article", mediatype: null })
                );
            },
            isSelected: () => queryParams.get("type") === "article",
        },
        {
            label: "Media",
            key: "media",
            onClick: () => {
                history.replace(
                    updateParams(location, { type: "media", mediatype: null })
                );
            },
            isSelected: () =>
                queryParams.get("type") === "media" &&
                !queryParams.has("mediatype"),
        },
        {
            label: "Page",
            key: "page",
            onClick: () => {
                history.replace(
                    updateParams(location, { type: "page", mediatype: null })
                );
            },
            isSelected: () => queryParams.get("type") === "page",
        },
        {
            label: "Image",
            key: "image",
            onClick: () => {
                history.replace(
                    updateParams(location, {
                        type: "media",
                        mediatype: "image",
                    })
                );
            },
            isSelected: () => queryParams.get("mediatype") === "image",
        },
        {
            label: "Gallery",
            key: "gallery",
            onClick: () => {
                history.replace(
                    updateParams(location, {
                        type: "media",
                        mediatype: "gallery",
                    })
                );
            },
            isSelected: () => queryParams.get("mediatype") === "gallery",
        },
        {
            label: "Video",
            key: "video",
            onClick: () => {
                history.replace(
                    updateParams(location, {
                        type: "media",
                        mediatype: "video",
                    })
                );
            },
            isSelected: () => queryParams.get("mediatype") === "video",
        },
        {
            label: "Audio",
            key: "audio",
            onClick: () => {
                history.replace(
                    updateParams(location, {
                        type: "media",
                        mediatype: "audio",
                    })
                );
            },
            isSelected: () => queryParams.get("mediatype") === "audio",
        },
        {
            label: "PDF",
            key: "pdf",
            onClick: () => {
                history.replace(
                    updateParams(location, { type: "media", mediatype: "pdf" })
                );
            },
            isSelected: () => queryParams.get("mediatype") === "pdf",
        },
    ];
    const onResetFilters = () => {
        history.replace(location.pathname + "?");
    };

    useEffect(() => {
        if (
            queryParams.has("type") ||
            queryParams.has("mediaType") ||
            queryParams.has("status") ||
            queryParams.has("collection") ||
            queryParams.has("workflow")
        ) {
            setIsFiltered(true);
        } else {
            setIsFiltered(false);
        }
    }, [queryParams]);

    return (
        <div className="z-auto flex flex-grow flex-wrap items-center justify-center gap-2 lg:justify-start">
            <div>
                <ButtonGroup
                    variant="xs"
                    buttons={[
                        <button
                            type="button"
                            key="filter-view-table"
                            selected={queryParams.get("view") !== "grid"}
                            onClick={() => {
                                history.replace(
                                    updateParams(location, { view: "table" })
                                );
                            }}
                        >
                            <ViewListIcon className="h-6 w-6 sm:h-4 sm:w-4" />
                            <span className="sr-only">View List</span>
                        </button>,
                        <button
                            type="button"
                            key="filter-view-table"
                            selected={queryParams.get("view") === "grid"}
                            onClick={() => {
                                history.replace(
                                    updateParams(location, { view: "grid" })
                                );
                            }}
                        >
                            <ViewGridIcon className="h-6 w-6 sm:h-4 sm:w-4" />
                            <span className="sr-only">View Grid</span>
                        </button>,
                    ]}
                />
            </div>
            <div>
                <ButtonGroup
                    variant="xs"
                    buttons={[
                        <button
                            type="button"
                            key="filter-status-all"
                            selected={!queryParams.get("status")}
                            onClick={() => {
                                history.replace(
                                    updateParams(location, { status: null })
                                );
                            }}
                        >
                            All
                        </button>,
                        ...filterItems.status.map((item) => (
                            <button
                                type="button"
                                key={"filter-status-" + item.value}
                                selected={
                                    queryParams.get("status") === item.value
                                }
                                onClick={() => {
                                    history.replace(
                                        updateParams(location, {
                                            status: item.value,
                                        })
                                    );
                                }}
                            >
                                {item.label}
                            </button>
                        )),
                    ]}
                />
            </div>
            <div>
                <ButtonGroup
                    variant="xs"
                    buttons={[
                        <button
                            type="menu"
                            key="filter-type-all"
                            buttons={typeFilters.map((item) => (
                                <button
                                    type="button"
                                    key={"filter-type-" + item.key}
                                    onClick={item.onClick}
                                >
                                    {item.label}
                                </button>
                            ))}
                        >
                            {queryParams.has("type") ||
                            queryParams.has("mediatype")
                                ? (
                                      <span className="font-semibold text-green-600">
                                          {
                                              typeFilters.find((f) =>
                                                  f.isSelected()
                                              )?.label
                                          }
                                      </span>
                                  ) || "Type"
                                : "Type"}
                        </button>,
                    ]}
                />
            </div>
            <div>
                <ButtonGroup
                    variant="xs"
                    buttons={[
                        <button
                            type="menu"
                            buttons={workflows.map((wf, index) => (
                                <button
                                    key={index}
                                    onClick={() => {
                                        history.replace(
                                            updateParams(location, {
                                                workflow: wf.id,
                                            })
                                        );
                                    }}
                                >
                                    {wf.name}
                                </button>
                            ))}
                        >
                            {queryParams.has("workflow") ? (
                                <span className="font-semibold text-green-600">
                                    {
                                        workflows.find(
                                            (wf) =>
                                                wf.id ===
                                                forceInt(
                                                    queryParams.get("workflow")
                                                )
                                        )?.name
                                    }
                                </span>
                            ) : (
                                "Workflow"
                            )}
                        </button>,
                    ]}
                />
            </div>
            <div>
                <ButtonGroup
                    variant="xs"
                    buttons={[
                        <button
                            type="menu"
                            buttons={[
                                ...collections.map((c, index) => (
                                    <button
                                        key={index}
                                        onClick={() => {
                                            history.replace(
                                                updateParams(location, {
                                                    collection: c.id,
                                                })
                                            );
                                        }}
                                    >
                                        {c.name}
                                    </button>
                                )),
                                <button
                                    className="border-t border-gray-300"
                                    onClick={() => {
                                        contextualHelp.showContextualHelp(
                                            "/search/collections.html"
                                        );
                                    }}
                                >
                                    Help with collections
                                </button>,
                            ]}
                        >
                            {queryParams.has("collection") ? (
                                <span className="font-semibold text-green-600">
                                    {
                                        collections.find(
                                            (c) =>
                                                c.id ===
                                                forceInt(
                                                    queryParams.get(
                                                        "collection"
                                                    )
                                                )
                                        )?.name
                                    }
                                </span>
                            ) : (
                                "Collection"
                            )}
                        </button>,
                    ]}
                />
            </div>
            <div className={isFiltered ? "" : "hidden"}>
                <ButtonGroup
                    variant="xs"
                    buttons={[
                        <button
                            type="button"
                            key="reset-filters"
                            onClick={onResetFilters}
                        >
                            Reset
                        </button>,
                    ]}
                />
            </div>
        </div>
    );
}
